// ThreadChip.tsx — 1.24.00 C2: the "Part 2 of 5 · Show this thread" chip a
// thread post carries wherever it appears (feed card and detail hero).
//
// EVERY member of a thread carries `post.thread`, the root included (the server
// serializes it at position 1) — so the chip has to decide for itself whether
// the link it is about to draw goes anywhere. 1.24.00 C2 fix round: on the
// root's own detail page it did not. It read "Part 1 of 5 · Show this thread"
// and linked to the page you were already on, and every part listed underneath
// drew the same dead link back up to that same root.
//
// The rule, in one place: a chip whose target is the post whose page you are on
// renders NOTHING. `ThreadPageScope` is how a detail page says which post that
// is; in the feed there is no scope, so the chip is always a real link (from the
// feed, a root linking to its own detail page is exactly right). Nothing is lost
// on the detail page — ThreadList sits directly below the hero and names every
// part, which is a better affordance than a chip that goes nowhere.
//
// Positions are stored once at create and never renumbered, so a deleted middle
// part leaves a gap: "Part 4 of 5" with only four posts left is honest about
// what happened rather than quietly resequencing somebody's writing.
//
// It is a real <Link>, which is also what stops PostCard.onCardClick from
// hijacking the click (that handler ignores anything inside an `a`).

import { createContext, useContext, type ReactNode } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import type { PostThread } from '@/core/models/post';
import { postPath } from '@/core/lib/postUrl';
import { Icon } from '@/icons';
import { MetaRow } from '@/ui';

const Chip = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: transparent;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  text-decoration: none;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
    background: ${({ theme }) => theme.color.hover};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

/**
 * The post whose detail page the subtree belongs to, or undefined in a feed.
 * Read by ThreadChip only — see the note above.
 */
const CurrentPostIdContext = createContext<string | undefined>(undefined);

export interface ThreadPageScopeProps {
  /** The resolved legacy id of the post this page is about (never a share code). */
  postId: string;
  children: ReactNode;
}

/** Marks a subtree as "the detail page OF `postId`". Renders no markup. */
export function ThreadPageScope({ postId, children }: ThreadPageScopeProps) {
  return <CurrentPostIdContext.Provider value={postId}>{children}</CurrentPostIdContext.Provider>;
}

export interface ThreadChipProps {
  thread: PostThread;
}

export function ThreadChip({ thread }: ThreadChipProps) {
  const currentPostId = useContext(CurrentPostIdContext);
  // A link to the page you are already on is not an affordance, it is a dud.
  if (currentPostId !== undefined && currentPostId === thread.rootId) return null;

  return (
    // 1.24.00 D1.1: `thread` carries the root's legacy id and nothing else — no
    // wire change here, deliberately — so this resolves to the legacy path and
    // the detail page relabels the address bar on arrival (D1.2). Routed through
    // postPath anyway so the post route shape is written in exactly one place.
    // Stays BARE through 1.24.50: the root's AUTHOR is not on `thread` either,
    // and this chip's own post's handle would be the wrong person on any thread
    // that is not your own.
    <Chip to={postPath({ id: thread.rootId })} data-testid="thread-chip">
      <Icon name="list-ordered" size={12} />
      <MetaRow>
        <span>
          Part {thread.position} of {thread.count}
        </span>
        <span>Show this thread</span>
      </MetaRow>
    </Chip>
  );
}
